import { WIX_INSTANCE_PARAMS } from '~actions/component_actions';
import axios from 'axios';
import { WidgetLoader } from '~loader/widget-loader';
import { isEmpty } from 'lodash';
import { Ticket } from 'widget/event_components/Ticket/index';
import { List, fromJS } from 'immutable';

export class TicketSpotContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      session: null,
      tickets: List(),
      occurrences: List(),
      calendarMetadata: null,
      calendarLoading: false,
      addons: List(),
      addonConfig: null,
      pagination: {
        limit: 5,
        offset: 0,
        total: 0,
        hasMore: false,
        loading: false,
        error: null,
      },
    };
  }

  async componentDidMount() {
    window.currency = this.props.event.get('currency');

    // Check if calendar view is enabled
    const isCalendarView = this.props.settings.get('timeBasedEventLayout') === 'desktop-full-calendar';

    if (this.props.event.get('is_time_based')) {
      if (isCalendarView) {
        await this.getCalendarMonth(); // Load default month (auto-detected)
      } else {
        await this.getTimedOccurrences();
      }
    } else if (this.props.settings.get('multiDayDateSelector') && this.props.event.get('is_series_event')) {
      await this.getRecurringSeries();
    } else {
      await this.createTickets();
    }

    // Load event add-ons if enabled
    const addonConfig = this.props.event.get('event_addon_config');
    if (addonConfig && addonConfig.get('enabled')) {
      await this.getEventAddons();
    }

    await this.createSession();
  }

  async getTimedOccurrences(offset = 0, append = false) {
    const id = this.props.event.get('id');
    const { limit } = this.state.pagination;

    this.setState({
      pagination: { ...this.state.pagination, loading: true, error: null },
    });

    try {
      const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/events/${id}/times${WIX_INSTANCE_PARAMS}`;
      const { data } = await axios.get(url, {
        params: { limit, offset },
        withCredentials: true,
      });

      const newOccurrences = fromJS(data.occurrences);

      this.setState(
        {
          occurrences: append ? this.state.occurrences.concat(newOccurrences) : newOccurrences,
          pagination: {
            limit: data.pagination.limit,
            offset: data.pagination.offset,
            total: data.pagination.total,
            hasMore: data.pagination.hasMore,
            loading: false,
            error: null,
          },
        },
        () => {
          this.props.onResizeComponent && this.props.onResizeComponent();
        }
      );
    } catch (error) {
      console.error('Error loading occurrences:', error);
      this.setState({
        pagination: { ...this.state.pagination, loading: false, error: error.message },
      });
    }
  }

  async getEventAddons() {
    const id = this.props.event.get('id');

    try {
      const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/events/${id}/addons${WIX_INSTANCE_PARAMS}`;
      const { data } = await axios.get(url, { withCredentials: true });

      this.setState(
        {
          addons: fromJS(data.addons),
          addonConfig: data.config,
        },
        () => {
          this.props.onResizeComponent && this.props.onResizeComponent();
        }
      );
    } catch (error) {
      console.error('Error loading event add-ons:', error);
    }
  }
  async getRecurringSeries() {
    const id = this.props.event.get('id');
    const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/events/${id}/occurrences${WIX_INSTANCE_PARAMS}`;
    const { data } = await axios.get(url, { withCredentials: true });
    this.setState(
      {
        occurrences: fromJS(data),
      },
      () => {
        this.props.onResizeComponent && this.props.onResizeComponent();
      }
    );
  }

  async getCalendarMonth(month = null, year = null, append = false) {
    const id = this.props.event.get('id');

    this.setState({ calendarLoading: true });

    try {
      const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/events/${id}/calendar${WIX_INSTANCE_PARAMS}`;
      const params = {};
      if (month) params.month = month;
      if (year) params.year = year;

      const { data } = await axios.get(url, {
        params,
        withCredentials: true,
      });

      const newOccurrences = fromJS(data.occurrences);

      this.setState(
        {
          occurrences: append ? this.state.occurrences.concat(newOccurrences) : newOccurrences,
          calendarMetadata: data.metadata,
          calendarLoading: false,
        },
        () => {
          this.props.onResizeComponent && this.props.onResizeComponent();
        }
      );
    } catch (error) {
      console.error('Error loading calendar month:', error);
      this.setState({ calendarLoading: false });
    }
  }

  loadMoreOccurrences = () => {
    if (!this.state.pagination.loading && this.state.pagination.hasMore) {
      const nextOffset = this.state.pagination.offset + this.state.pagination.limit;
      this.getTimedOccurrences(nextOffset, true);
    }
  };

  loadNextMonth = () => {
    if (!this.state.calendarMetadata) return;

    const { month, year } = this.state.calendarMetadata;
    let nextMonth = month + 1;
    let nextYear = year;

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }

    this.getCalendarMonth(nextMonth, nextYear, false);
  };

  loadPreviousMonth = () => {
    if (!this.state.calendarMetadata) return;

    const { month, year } = this.state.calendarMetadata;
    let prevMonth = month - 1;
    let prevYear = year;

    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear -= 1;
    }

    this.getCalendarMonth(prevMonth, prevYear, false);
  };

  handleCalendarMonthChange = (month, year) => {
    // Only load if month/year actually changed
    if (this.state.calendarMetadata) {
      const { month: currentMonth, year: currentYear } = this.state.calendarMetadata;
      if (month === currentMonth && year === currentYear) {
        return; // No change, don't reload
      }
    }
    this.getCalendarMonth(month, year, false);
  };

  async createSession() {
    const id = this.props.event.get('id');

    const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/sessions/events/${id}${WIX_INSTANCE_PARAMS}`;
    const { data } = await axios.get(url, { withCredentials: true });
    this.setState(
      {
        session: data,
      },
      () => {
        this.props.onResizeComponent && this.props.onResizeComponent();
      }
    );
  }
  async createTickets() {
    const id = this.props.event.get('id');
    const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/events/${id}/tickets${WIX_INSTANCE_PARAMS}`;
    const { data } = await axios.get(url, { withCredentials: true });

    this.setState(
      {
        tickets: fromJS(data.tickets),
        hasAccessCodeControllerTickets: data.hasAccessCodeControllerTickets,
      },
      () => {
        if (this.props.onResizeComponent) {
          setTimeout(() => {
            this.props.onResizeComponent();
          }, 2000);
        }
      }
    );
  }

  handleSessionDisconnect = async () => {
    const url = `${process.env.EVENTVIEWER_SERVER}/api/v1/sessions${WIX_INSTANCE_PARAMS}`;
    await axios.delete(url, { withCredentials: true });
    this.setState({
      session: { ...this.state.session, attendee: null, cards: null },
    });
  };

  render() {
    const isMobile = window.isMobileType;

    if (!this.state.session) {
      return <WidgetLoader loading={isEmpty(this.state.session)} isRelative />;
    }
    return (
      <div style={{ marginTop: isMobile ? 0 : 21, width: '100%' }}>
        <Ticket
          {...this.props}
          tickets={this.state.tickets}
          hasAccessCodeControllerTickets={this.state.hasAccessCodeControllerTickets}
          occurrences={this.state.occurrences}
          calendarMetadata={this.state.calendarMetadata}
          calendarLoading={this.state.calendarLoading}
          onLoadNextMonth={this.loadNextMonth}
          onLoadPreviousMonth={this.loadPreviousMonth}
          onCalendarMonthChange={this.handleCalendarMonthChange}
          pagination={this.state.pagination}
          onLoadMoreOccurrences={this.loadMoreOccurrences}
          session={this.state.session}
          onSessionDisconnect={this.handleSessionDisconnect}
          addons={this.state.addons}
          addonConfig={this.state.addonConfig}
        />
      </div>
    );
  }
}
